import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { InputAdornmentProps, keyframes } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  getIndividualByUserId,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import { Moment } from "moment";
import { useState } from "react";
import { useSelector } from "react-redux";
import UserInformation from "users/information/user-information";
import { MaskedTeam } from "work/_shared/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { TextInput } from "work/proposals/_views/drafts/text-input/text-input";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import ClientSelector from "work/work-agreements/teams/client-team/_views/client-selector";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import Proposal from "work/proposals/proposal";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
  display: "grid",
  gap: theme.spacing(6),
  gridTemplateColumns: "1fr 2fr",
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(3),
  width: "100%",
}));
const GeneralSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginLeft: theme.spacing(0.5),
}));
const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(1),
  "& .MuiFormControl-root": {
    marginTop: theme.spacing(1),
  },
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: theme.spacing(50),
}));
const NameTextField = styled(TextInput, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: theme.spacing(50),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const DescriptionTextField = styled(TextInput, {
  shouldForwardProp: (prop) => prop !== "active",
})<{
  active?: boolean;
}>(({ theme, active }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(50),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const NegotiableCheckbox = styled("div")(() => ({
}));
const DateContainer = styled("div")(({ theme }) => ({
  display: "flex",
  margin: theme.spacing(1, 0),
  width: "100%",
}));
const DateControlLabel = styled(FormControlLabel)(() => ({
  width: "100%",
}));
const DateFieldButtons = styled("span")(() => ({
  alignItems: "center",
  flexDirection: "row",
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  "& > :first-of-type": {
    marginRight: 0,
  },
}));
const HighlightDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== "active",
})<{
  active?: boolean;
}>(({ active }) => ({
  ...(active && {
    "& fieldset": {
      animation: `${pulseBorder} 1.5s ease-in-out infinite`,
    },
  }),
}));

type DetailsTabProps = {
  proposal?: Proposal;
  activeReviewField?: ProposalField;
  proposalBuilder: ProposalBuilder;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  commentThreads: CommentThread[];
  isRequesting?: boolean;
  isSaved: boolean;
  onProposalBuilderUpdated: (newBuilder: ProposalBuilder) => void;
  onSelectingClientChange: (isSelectingClient: boolean) => void;
  onCommentsClicked?: (field: ProposalField, name?: string) => void;
};

export default function DetailsTab(props: Readonly<DetailsTabProps>) {
  const {
    proposal,
    activeReviewField,
    proposalBuilder,
    issues,
    disableEditing,
    commentThreads,
    isRequesting,
    isSaved,
    onProposalBuilderUpdated,
    onSelectingClientChange,
    onCommentsClicked,
  } = props;

  const [isNegotiable, setIsNegotiable] = useState<boolean>(proposalBuilder.currentSpec.negotiable.value ?? true);
  const [isUsingResponseDueBy, setIsUsingResponseDueBy] = useState<boolean>(
    Boolean(proposalBuilder.currentSpec.responseDueBy),
  );
  const [isUsingStartDate, setIsUsingStartDate] = useState<boolean>(
    Boolean(proposalBuilder.currentSpec.workAgreement.startDate),
  );
  const [isUsingEndDate, setIsUsingEndDate] = useState<boolean>(
    Boolean(proposalBuilder.currentSpec.workAgreement.endDate),
  );
  const [startDate, setStartDate] = useState<AHDate | null>(
    proposalBuilder.currentSpec.workAgreement.startDate ?? null,
  );
  const [endDate, setEndDate] = useState<AHDate | null>(proposalBuilder.currentSpec.workAgreement.endDate ?? null);
  const [isSelectingClient, setIsSelectingClient] = useState<boolean>(false);
  const [clientSelectorAnchor, setClientSelectorAnchor] = useState<HTMLDivElement>();

  const clientLeaderUserId = (proposalBuilder.currentSpec.workAgreement.clientTeam as ClientTeam)?.leader?.userId;
  const clientLeaderIndividual = useSelector(getIndividualByUserId(clientLeaderUserId));
  const commentsToolTip = isSaved ? "Add comments" : "Save proposal to add comments";

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(clientLeaderUserId ? [clientLeaderUserId.value] : []) },
    { skip: !clientLeaderUserId || !!clientLeaderIndividual }
  );

  function getMinValidDate(compareDate: AHDate, daysToAdd: number = 0) {
    const currentDateValue = new AHDate().value;
    const compareDateValue = compareDate.value;

    return new AHDate(Math.max(currentDateValue, compareDateValue)).add(daysToAdd, "day");
  }

  function handleSelectClientClicked(event: React.MouseEvent<HTMLDivElement>) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setClientSelectorAnchor(inputElements.length > 0 ? inputElements[0] : event.currentTarget);
    setIsSelectingClient(true);
    onSelectingClientChange(true);
  }

  async function handleClientUpdated(userInfo?: UserInformation) {
    if (!userInfo?.id || !userInfo?.legalEntityId || !userInfo?.name) {
      onProposalBuilderUpdated(proposalBuilder.setClient());
    } else {
      onProposalBuilderUpdated(
        proposalBuilder.setClient(
          new EntityMember({
            legalEntityId: userInfo.legalEntityId,
            userId: userInfo.id,
          }),
        ),
      );
    }
  }

  function renderDateFieldButtons(params: InputAdornmentProps, field: ProposalField) {
    return (
      <DateFieldButtons>
        {params.children}
        <CommentsButton
          field={field}
          isActive={activeReviewField?.isEqualTo(field) ?? false}
          disabled={!isSaved}
          toolTip={commentsToolTip}
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                onCommentsClicked(field);
              }
              : undefined
          }
          commentThreads={commentThreads}
        />
      </DateFieldButtons>
    );
  }

  return (
    <ProposalContainer>
      <GeneralSection>
        {proposal && (
          <Typography variant="h6" sx={{ fontWeight: "normal", mb: 2 }}>
            <strong>Status</strong>: {proposal?.displayStatus}
          </Typography>
        )}
        {(() => {
          let clientValue: string = "";
          if (MaskedTeam.isMasked(proposalBuilder.currentSpec.workAgreement.clientTeam)) {
            clientValue = "Hidden";
          } else if (clientLeaderIndividual) {
            clientValue = clientLeaderIndividual.fullName;
          } else if (proposalBuilder.currentSpec.workAgreement.clientTeam) {
            clientValue = "Client Team Representative";
          }

          return (
            <ClientTextField
              disabled={disableEditing || isSelectingClient || clientSelectorAnchor !== undefined}
              label="Client Representative"
              autoComplete="off"
              error={issues?.entries.some((entry) => entry.field === ProposalField.Client)}
              helperText={issues?.entries
                .filter((entry) => entry.field === ProposalField.Client)
                .map((entry) => entry.description)
                .join("\n")}
              required
              onClick={handleSelectClientClicked}
              value={clientValue}
              slotProps={{
                input: {
                  endAdornment: (
                    <>
                      {!disableEditing && proposalBuilder.currentSpec.workAgreement.clientTeam && (
                        <Tooltip title="Remove Client">
                          <span>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                onProposalBuilderUpdated(proposalBuilder.setClient());
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  ),
                },
                inputLabel: {
                  shrink: Boolean(proposalBuilder.currentSpec.workAgreement.clientTeam),
                },
              }}
            />
          );
        })()}
        <NameTextField
          label="Proposal Name"
          required={true}
          disabled={disableEditing}
          active={activeReviewField?.isEqualTo(ProposalField.Name) ?? false}
          initialValue={proposalBuilder.currentSpec.workAgreement.name?.value ?? ""}
          error={issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.Name))}
          helperText={issues?.entries
            .filter((entry) => entry.field.isEqualTo(ProposalField.Name))
            .map((entry) => entry.description)
            .join("\n")}
          slotProps={{
            input: {
              endAdornment: (
                <CommentsButton
                  field={ProposalField.Name}
                  isActive={activeReviewField?.isEqualTo(ProposalField.Name) ?? false}
                  disabled={!isSaved}
                  toolTip={commentsToolTip}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                        onCommentsClicked(ProposalField.Name);
                      }
                      : undefined
                  }
                  commentThreads={commentThreads}
                />
              ),
            },
          }}
          onChange={(newValue) => {
            onProposalBuilderUpdated(proposalBuilder.setName(new ProjectName(newValue)));
          }}
        />
        <DescriptionTextField
          label="Description"
          required={true}
          multiline={true}
          rows={3}
          disabled={disableEditing}
          active={activeReviewField?.isEqualTo(ProposalField.Description)}
          initialValue={proposalBuilder.currentSpec.workAgreement.description?.value ?? ""}
          error={issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.Description))}
          helperText={issues?.entries
            .filter((entry) => entry.field.isEqualTo(ProposalField.Description))
            .map((entry) => entry.description)
            .join("\n")}
          slotProps={{
            input: {
              endAdornment: (
                <CommentsButton
                  field={ProposalField.Description}
                  isActive={activeReviewField?.isEqualTo(ProposalField.Description) ?? false}
                  disabled={!isSaved}
                  toolTip={commentsToolTip}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                        onCommentsClicked(ProposalField.Description);
                      }
                      : undefined
                  }
                  commentThreads={commentThreads}
                />
              ),
            },
          }}
          onChange={(newValue) => {
            onProposalBuilderUpdated(proposalBuilder.setDescription(new ProjectDescription(newValue)));
          }}
        />
      </GeneralSection>
      {!isRequesting && (
        <DateSection>
          <NegotiableCheckbox>
            <FormControlLabel
              label="Negotiable"
              control={
                <Checkbox
                  color="primary"
                  checked={isNegotiable}
                  disabled={disableEditing}
                  onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    setIsNegotiable(checked);
                    onProposalBuilderUpdated(proposalBuilder.setNegotiable(checked));
                  }}
                />
              }
            />
          </NegotiableCheckbox>
          <DateContainer>
            <span>
              <DateControlLabel
                slotProps={{
                  typography: {
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  },
                }}
                label={
                  isUsingResponseDueBy ? null : (
                    <>
                      <span>Set a response due date?</span>
                      <span>
                        <CommentsButton
                          field={ProposalField.ResponseDueBy}
                          disabled={!isSaved}
                          isActive={activeReviewField?.isEqualTo(ProposalField.ResponseDueBy) ?? false}
                          toolTip={commentsToolTip}
                          onCommentsClicked={
                            onCommentsClicked
                              ? () => {
                                onCommentsClicked(ProposalField.ResponseDueBy);
                              }
                              : undefined
                          }
                          commentThreads={commentThreads}
                        />
                      </span>
                    </>
                  )
                }
                control={
                  <Checkbox
                    color="primary"
                    required={false}
                    disabled={disableEditing}
                    checked={isUsingResponseDueBy}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      setIsUsingResponseDueBy(checked);
                      const newDate = checked ? new AHDate().add(1, "day") : undefined;
                      onProposalBuilderUpdated(proposalBuilder.setResponseDueBy(newDate));
                    }}
                  />
                }
              />
            </span>
            {isUsingResponseDueBy && (
              <HighlightDatePicker
                label="Response Due Date"
                active={activeReviewField?.isEqualTo(ProposalField.ResponseDueBy)}
                disabled={disableEditing}
                value={proposalBuilder.currentSpec.responseDueBy?.asMoment ?? null}
                minDate={new AHDate().asMoment}
                slots={{
                  inputAdornment: (params) => renderDateFieldButtons(params, ProposalField.ResponseDueBy),
                }}
                slotProps={{
                  openPickerIcon: {
                    color: "primary",
                  },
                  textField: {
                    error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy)),
                    helperText: issues?.entries
                      .filter((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy))
                      .map((entry) => entry.description)
                      .join("\n"),
                  },
                }}
                onChange={(date: Moment | null) => {
                  onProposalBuilderUpdated(
                    proposalBuilder.setResponseDueBy(date ? new AHDate(date.endOf("day").toISOString()) : undefined),
                  );
                }}
              />
            )}
          </DateContainer>
          <DateContainer>
            <span>
              <DateControlLabel
                slotProps={{
                  typography: {
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  },
                }}
                label={
                  isUsingStartDate ? null : (
                    <>
                      <span>Set a start date?</span>
                      <span>
                        <CommentsButton
                          field={ProposalField.StartDate}
                          isActive={activeReviewField?.isEqualTo(ProposalField.StartDate) ?? false}
                          disabled={!isSaved}
                          toolTip={commentsToolTip}
                          onCommentsClicked={
                            onCommentsClicked
                              ? () => {
                                onCommentsClicked(ProposalField.StartDate);
                              }
                              : undefined
                          }
                          commentThreads={commentThreads}
                        />
                      </span>
                    </>
                  )
                }
                control={
                  <Checkbox
                    color="primary"
                    disabled={disableEditing}
                    required={false}
                    checked={isUsingStartDate}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      setIsUsingStartDate(checked);
                      const newDate = checked
                        ? new AHDate(
                          proposalBuilder.currentSpec.responseDueBy?.toISOString() ??
                          new AHDate().add(1, "day").toISOString(),
                        )
                        : undefined;
                      setStartDate(newDate ?? null);
                      onProposalBuilderUpdated(proposalBuilder.setStartDate(newDate));
                    }}
                  />
                }
              />
            </span>

            {isUsingStartDate && (
              <HighlightDatePicker
                label="Start Date"
                active={activeReviewField?.isEqualTo(ProposalField.StartDate)}
                disabled={disableEditing}
                value={startDate?.asMoment}
                minDate={new AHDate().asMoment}
                slots={{
                  inputAdornment: (params) => renderDateFieldButtons(params, ProposalField.StartDate),
                }}
                slotProps={{
                  openPickerIcon: {
                    color: "primary",
                  },
                  textField: {
                    error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.StartDate)),
                    helperText: issues?.entries
                      .filter((entry) => entry.field.isEqualTo(ProposalField.StartDate))
                      .map((entry) => entry.description)
                      .join("\n"),
                  },
                }}
                onChange={(date: Moment | null) => {
                  const newStartDate = date?.endOf("day");
                  setStartDate(newStartDate ? new AHDate(newStartDate.toISOString()) : null);
                  onProposalBuilderUpdated(
                    proposalBuilder.setStartDate(newStartDate ? new AHDate(newStartDate.toISOString()) : undefined),
                  );
                }}
              />
            )}
          </DateContainer>
          <DateContainer>
            <span>
              <DateControlLabel
                slotProps={{
                  typography: {
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  },
                }}
                label={
                  isUsingEndDate ? null : (
                    <>
                      <span>Set a end date?</span>
                      <CommentsButton
                        field={ProposalField.EndDate}
                        isActive={activeReviewField?.isEqualTo(ProposalField.EndDate) ?? false}
                        disabled={!isSaved}
                        toolTip={commentsToolTip}
                        onCommentsClicked={
                          onCommentsClicked
                            ? () => {
                              onCommentsClicked(ProposalField.EndDate);
                            }
                            : undefined
                        }
                        commentThreads={commentThreads}
                      />
                    </>
                  )
                }
                control={
                  <Checkbox
                    color="primary"
                    required={false}
                    disabled={disableEditing}
                    checked={isUsingEndDate}
                    onChange={(_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                      setIsUsingEndDate(checked);
                      const newDate = checked
                        ? new AHDate(startDate?.toISOString() ?? new AHDate().add(1, "day").toISOString())
                        : undefined;
                      onProposalBuilderUpdated(proposalBuilder.setEndDate(newDate));
                    }}
                  />
                }
              />
            </span>
            {isUsingEndDate && (
              <HighlightDatePicker
                label="End Date"
                active={activeReviewField?.isEqualTo(ProposalField.EndDate)}
                disabled={disableEditing}
                value={endDate?.asMoment}
                minDate={
                  getMinValidDate(startDate ?? proposalBuilder.currentSpec.responseDueBy ?? new AHDate().add(1, "day"))
                    .asMoment
                }
                slots={{
                  inputAdornment: (params) => renderDateFieldButtons(params, ProposalField.EndDate),
                }}
                slotProps={{
                  openPickerIcon: {
                    color: "primary",
                  },
                  textField: {
                    error: issues?.entries.some((entry) => entry.field.isEqualTo(ProposalField.EndDate)),
                    helperText: issues?.entries
                      .filter((entry) => entry.field.isEqualTo(ProposalField.EndDate))
                      .map((entry) => entry.description)
                      .join("\n"),
                  },
                }}
                onChange={(date: Moment | null) => {
                  const newEndDate = date?.endOf("day");
                  setEndDate(newEndDate ? new AHDate(newEndDate.toISOString()) : null);
                  onProposalBuilderUpdated(
                    proposalBuilder.setEndDate(newEndDate ? new AHDate(newEndDate.toISOString()) : undefined),
                  );
                }}
              />
            )}
          </DateContainer>
        </DateSection>
      )}
      <ClientSelector
        clientUserId={
          MaskedTeam.isMasked(proposalBuilder.currentSpec.workAgreement.clientTeam)
            ? undefined
            : proposalBuilder.currentSpec.workAgreement.clientTeam?.leader?.userId
        }
        popoverAnchor={clientSelectorAnchor}
        onPopoverClose={() => {
          setClientSelectorAnchor(undefined);
          setIsSelectingClient(false);
          onSelectingClientChange(false);
        }}
        onClientUpdated={handleClientUpdated}
      />
    </ProposalContainer>
  );
}
